import React, { useState } from 'react';
import { RadioGroup } from '@headlessui/react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  CheckIcon, 
  SparklesIcon, 
  BoltIcon, 
  TicketIcon, 
  CreditCardIcon,
  AcademicCapIcon,
  TagIcon,
  XMarkIcon,
  ArrowRightIcon,
  CheckCircleIcon,
  ShieldCheckIcon,
  UserPlusIcon
} from '@heroicons/react/24/solid';
import { useRedirect } from '../../Shared/Hooks/useRedirect ';
import { LandingHeader } from '../../LandingScreens/Components/LandingHeader';

// Configurable Plans Data Array
export interface LocalPlan {
  id: string;
  name: string;
  planCode: string;
  originalPrice: number;
  discountPrice: number;
  billingCycle: string;
  credits: number;
  priority?: number;
  isPopular?: boolean;
  features: string[];
}

const PLANS_DATA: LocalPlan[] = [
  {
    id: 'ignite',
    name: 'IGNITE',
    planCode: 'PRE-LAUNCH-01',
    originalPrice: 9999,
    discountPrice: 4999,
    billingCycle: 'mo',
    credits: 30,
    priority: 1,
    isPopular: false,
    features: [
      '10 Job Postings',
      'Basic Candidate Sourcing',
      'Email Support',
      'Standard Candidate Pool Access',
      '7 Days Post Visibility'
    ]
  },
  {
    id: 'accelerate',
    name: 'ACCELERATE',
    planCode: 'PRE-LAUNCH-02',
    originalPrice: 24999,
    discountPrice: 14999,
    billingCycle: 'mo',
    credits: 100,
    priority: 2,
    isPopular: true,
    features: [
      '25 Job Postings',
      'AI Candidate Matching',
      'Priority Email & Chat Support',
      'Full Candidate Database Access',
      '30 Days Post Visibility',
      'Custom Branding Options'
    ]
  },
  {
    id: 'dominate',
    name: 'DOMINATE',
    planCode: 'PRE-LAUNCH-03',
    originalPrice: 49999,
    discountPrice: 29999,
    billingCycle: 'mo',
    credits: 300,
    priority: 3,
    isPopular: false,
    features: [
      '60 Job Postings',
      'Dedicated Account Manager',
      'Unlimited Candidate Database Access',
      '60 Days Post Visibility',
      'Automated Interview Scheduling',
      'Advanced Analytics & Reports'
    ]
  }
];

interface StaticSubscriptionUIProps {
  onSelectPlan?: (plan: LocalPlan) => void;
  onNavigateToRegister?: (plan: LocalPlan) => void;
}

const StaticSubscriptionUI: React.FC<StaticSubscriptionUIProps> = ({ 
  onSelectPlan, 
  onNavigateToRegister 
}) => {
  const [selected, setSelected] = useState<LocalPlan>(PLANS_DATA[1]); // Default to ACCELERATE
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);

  const handleSelection = (plan: LocalPlan) => {
    setSelected(plan);
    if (onSelectPlan) {
      onSelectPlan(plan);
    }
  };

  const handleAction = () => {
    setIsModalOpen(true);
  };

  const redirect = useRedirect();

  const handleRegisterConfirm = () => {
    setIsModalOpen(false);
    if (onNavigateToRegister) {
      onNavigateToRegister(selected);
    } else {
      // Add your fallback registration navigation logic here (e.g. window.location.href = '/register')
      // console.log('Redirecting to registration with plan:', selected.name);
      redirect('/early-access');
    }
  };

  const preLaunchBenefits = [
    'Explore available membership plans tailored to your hiring scale',
    'Unlock exclusive 50% flat pre-launch founding pricing',
    'Access recruiter and executive leadership hiring opportunities',
    'Manage subscriptions and job posting allocations seamlessly from your dashboard',
    'Receive priority early access to new AI candidate-matching platform features'
  ];

  const renderPlanCard = (plan: LocalPlan) => {
    const isChecked = selected.id === plan.id;

    return (
      <RadioGroup.Option
        key={plan.id}
        value={plan}
        className={`
          relative flex flex-col p-1 transition-all duration-300 cursor-pointer rounded-[2.5rem]
          ${isChecked 
            ? 'bg-gradient-to-b from-indigo-500 via-purple-500 to-pink-500 shadow-[0_0_50px_-12px_rgba(99,102,241,0.5)] scale-[1.02] z-10' 
            : 'bg-slate-800/40 hover:bg-slate-800/80 hover:scale-[1.01] border border-slate-800'}
        `}
      >
        <div className="bg-slate-900 rounded-[2.4rem] p-8 h-full flex flex-col justify-between">
          <div>
            {plan.isPopular && (
              <div className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                <span className="flex items-center gap-1.5 px-4 py-1 rounded-full text-[10px] font-black uppercase tracking-wider bg-gradient-to-r from-amber-300 to-yellow-500 text-slate-950 shadow-xl border border-white/20">
                  <SparklesIcon className="w-3.5 h-3.5 text-slate-950" /> POPULAR CHOICE
                </span>
              </div>
            )}

            <div className="flex justify-between items-start">
              <div>
                <p className="text-indigo-400 font-mono text-xs font-bold tracking-wider uppercase mb-1">
                  {plan.planCode}
                </p>
                <h3 className="text-2xl font-bold text-white tracking-tight">{plan.name}</h3>
              </div>
              <div className={`p-2.5 rounded-2xl ${isChecked ? 'bg-indigo-500/20 text-indigo-400' : 'bg-slate-800 text-slate-500'}`}>
                <BoltIcon className="w-6 h-6" />
              </div>
            </div>

            {/* Discount Badge */}
            <div className="mt-4 inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-xs font-black tracking-wider uppercase">
              <TagIcon className="w-3.5 h-3.5" /> 50% FLAT PRE-LAUNCH DISCOUNT
            </div>

            <div className="mt-4 flex items-baseline gap-3">
              <span className="text-5xl font-black tracking-tight text-white">
                ₹{plan.discountPrice.toLocaleString('en-IN')}
              </span>
              <span className="text-xl text-slate-500 line-through font-semibold">
                ₹{plan.originalPrice.toLocaleString('en-IN')}
              </span>
              <span className="text-slate-400 font-medium text-sm lowercase">/{plan.billingCycle}</span>
            </div>

            <div className="mt-4 inline-flex items-center gap-2 px-3 py-1.5 rounded-xl bg-indigo-500/10 border border-indigo-500/20 text-indigo-300 text-xs font-bold">
              <TicketIcon className="w-4 h-4 text-indigo-400" />
              {plan.credits} Job Credits Included
            </div>

            <div className="mt-8 pt-6 border-t border-slate-800/80 space-y-3">
              {plan.features.map((feat, i) => (
                <div key={i} className="flex items-center text-xs text-slate-300 font-medium">
                  <CheckIcon className="w-4 h-4 mr-2.5 text-indigo-400 flex-shrink-0" />
                  {feat}
                </div>
              ))}
            </div>
          </div>

          <div
            className={`mt-10 w-full py-3.5 px-6 rounded-2xl font-black text-xs uppercase text-center tracking-widest transition-all duration-300 ${
              isChecked
                ? 'bg-gradient-to-r from-indigo-500 to-purple-600 text-white shadow-lg shadow-indigo-500/25'
                : 'bg-slate-800/90 text-slate-300 hover:bg-slate-700'
            }`}
          >
            {isChecked ? 'Selected Plan' : 'Select Plan'}
          </div>
        </div>
      </RadioGroup.Option>
    );
  };

  return (
    <>
    <LandingHeader />
    <div className="relative min-h-screen bg-slate-950 px-4 pb-56 pt-20 text-white sm:px-6 sm:pb-44 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Main Header */}
        <div className="text-center mb-16">
          <motion.div 
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="inline-block bg-gradient-to-r from-indigo-500/10 to-purple-500/10 border border-indigo-500/20 px-4 py-1.5 rounded-full mb-4"
          >
            <span className="text-indigo-400 font-bold tracking-widest uppercase text-xs">
              🚀 Limited Time Pre-Launch Offer
            </span>
          </motion.div>
          <motion.h1
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-4xl font-extrabold tracking-tight sm:text-6xl bg-clip-text text-transparent bg-gradient-to-r from-white via-slate-200 to-indigo-400"
          >
            Register Early. Get 50% Off.
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="mt-4 text-slate-400 text-sm sm:text-base max-w-2xl mx-auto"
          >
            Lock in early recruiter benefits today. Pre-register your account to receive 50% flat discount on all standard plans at official launch.
          </motion.p>
        </div>

        <RadioGroup value={selected} onChange={handleSelection} className="space-y-20">
          <section className="space-y-6">
            <div className="flex items-center gap-3 border-b border-slate-800 pb-4">
              <div className="p-2 rounded-xl bg-indigo-500/10 border border-indigo-500/20 text-indigo-400">
                <AcademicCapIcon className="w-6 h-6" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-white tracking-wide">Recruiter Pre-Launch Tier</h3>
                <p className="text-xs text-slate-400">Select a tier below to reserve your early bird pricing</p>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-y-10 sm:grid-cols-2 sm:gap-x-6 lg:grid-cols-3 lg:gap-x-8">
              {PLANS_DATA.map(renderPlanCard)}
            </div>
          </section>
        </RadioGroup>
      </div>

      {/* Floating Action Bar.

          Centred with auto margins, not `-translate-x-1/2`. framer-motion writes its own inline
          `transform` for the y animation, which replaces the Tailwind transform entirely - so the
          old `left-1/2 -translate-x-1/2` lost its -50% shift and hung half off the right edge. */}
      <AnimatePresence>
        {selected && (
          <motion.div 
            initial={{ y: 100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 100, opacity: 0 }}
            className="fixed bottom-4 left-0 right-0 z-40 mx-auto w-[92%] max-w-2xl sm:bottom-8"
          >
            <div className="flex flex-col items-center gap-3 rounded-3xl border border-white/10 bg-slate-900/90 p-4 text-center shadow-2xl backdrop-blur-xl sm:flex-row sm:justify-between sm:gap-6 sm:px-8 sm:text-left">
              <div className="w-full min-w-0 sm:w-auto">
                <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 sm:text-xs">Selected Pre-Launch Tier</p>
                <p className="break-words text-sm font-bold text-white sm:text-base">{selected.name} • {selected.credits} Credits</p>
              </div>
              <div className="flex w-full flex-col items-center gap-3 sm:w-auto sm:flex-row sm:items-center sm:justify-end sm:gap-6">
                <div className="text-center sm:text-right">
                  <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 sm:text-xs">Early Bird Price</p>
                  <div className="flex items-baseline justify-center gap-2 sm:justify-end">
                    <p className="text-lg font-black text-indigo-400 sm:text-xl">₹{selected.discountPrice.toLocaleString('en-IN')}</p>
                    <p className="text-[11px] text-slate-500 line-through sm:text-xs">₹{selected.originalPrice.toLocaleString('en-IN')}</p>
                  </div>
                </div>
                <button
                  onClick={handleAction}
                  className="flex w-full items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-indigo-500 to-purple-600 px-5 py-3 text-xs font-black uppercase tracking-wider text-white shadow-lg shadow-indigo-500/25 transition-all hover:from-indigo-400 hover:to-purple-500 active:scale-95 sm:w-auto sm:px-8 sm:text-sm"
                >
                  <CreditCardIcon className="h-4 w-4 sm:h-5 sm:w-5" />
                  Pre-Register
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* PRE-LAUNCH INFORMATION MODAL */}
      <AnimatePresence>
        {isModalOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6 overflow-y-auto">
            {/* Dark Backdrop */}
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsModalOpen(false)}
              className="fixed inset-0 bg-slate-950/80 backdrop-blur-md"
            />

            {/* Modal Box */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              transition={{ duration: 0.2 }}
              className="relative w-full max-w-lg bg-slate-900 border border-indigo-500/30 rounded-[2.5rem] shadow-2xl p-6 sm:p-8 overflow-hidden z-10"
            >
              {/* Background Accent Glow */}
              <div className="absolute -top-24 -right-24 w-48 h-48 bg-indigo-500/20 rounded-full blur-3xl pointer-events-none" />
              <div className="absolute -bottom-24 -left-24 w-48 h-48 bg-purple-500/20 rounded-full blur-3xl pointer-events-none" />

              {/* Close Button */}
              <button 
                onClick={() => setIsModalOpen(false)}
                className="absolute top-6 right-6 p-2 rounded-full bg-slate-800 text-slate-400 hover:text-white hover:bg-slate-700 transition-colors"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>

              {/* Header */}
              <div className="flex items-center gap-3.5 mb-6">
                <div className="p-3 rounded-2xl bg-indigo-500/10 border border-indigo-500/20 text-indigo-400">
                  <ShieldCheckIcon className="w-7 h-7" />
                </div>
                <div>
                  <span className="text-[10px] font-black uppercase tracking-widest text-indigo-400">
                    Pre-Launch Invitation
                  </span>
                  <h3 className="text-xl sm:text-2xl font-bold text-white tracking-tight">
                    Interest Registered!
                  </h3>
                </div>
              </div>

              {/* Sub-Header text */}
              <p className="text-slate-300 text-sm leading-relaxed mb-6">
                Thank you for your interest in the <span className="font-bold text-indigo-400">{selected.name}</span> plan! To access Nexgent's pre-launch offers and founding member benefits, please complete your registration first.
              </p>

              {/* Benefits Section */}
              <div className="bg-slate-950/60 rounded-2xl p-4 sm:p-5 border border-slate-800 space-y-3 mb-8">
                <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-1 flex items-center gap-1.5">
                  <SparklesIcon className="w-4 h-4 text-amber-400" />
                  What you'll unlock after registering:
                </p>
                {preLaunchBenefits.map((benefit, idx) => (
                  <div key={idx} className="flex items-start gap-3">
                    <CheckCircleIcon className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
                    <span className="text-xs sm:text-sm text-slate-300 font-medium">
                      {benefit}
                    </span>
                  </div>
                ))}
              </div>

              {/* Action Footer */}
              <div className="flex flex-col sm:flex-row items-center gap-3">
                <button
                  onClick={handleRegisterConfirm}
                  className="w-full sm:flex-1 py-3.5 px-6 rounded-2xl bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-400 hover:to-purple-500 text-white font-black text-xs uppercase tracking-widest flex items-center justify-center gap-2 shadow-lg shadow-indigo-500/25 transition-all active:scale-95"
                >
                  <UserPlusIcon className="w-4 h-4" />
                  Create Account Today
                  <ArrowRightIcon className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setIsModalOpen(false)}
                  className="w-full sm:w-auto py-3.5 px-6 rounded-2xl bg-slate-800 hover:bg-slate-700 text-slate-300 font-bold text-xs uppercase tracking-wider transition-colors"
                >
                  Close
                </button>
              </div>

              <p className="text-center text-[11px] text-slate-500 mt-4">
                Secure your place in Nexgent's private leadership network.
              </p>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
    </>
  );
};

export default StaticSubscriptionUI;